import { useQuery } from "@tanstack/react-query";
import { LayoutModule, LayoutResponse } from "../../../types";
import {
  ExtraQueryParams,
  LayoutQueryParams,
  getLayoutResourceApi,
} from "../../../api/home.api";

export type ModuleSelector = { module?: string; alias?: string };

export type NormalizeFn<T> = (data: any[]) => T[];

export type LayoutSelectors<TContent> = {
  selectors?: Partial<Record<keyof TContent, ModuleSelector>>;
  normalizers?: Partial<Record<keyof TContent, NormalizeFn<any>>>;
};

const normalizeMediaValue = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value.replace(/\\/g, "");
  if (Array.isArray(value) && value.length > 0)
    return normalizeMediaValue(value[0]);
  if (typeof value === "object") {
    const url = value?.url || value?.path || value?.src || value?.value || "";
    const filename = value?.filename || value?.name || "";
    if (url && filename) {
      const cleanUrl = typeof url === "string" ? url.replace(/\\/g, "") : "";
      const cleanFilename =
        typeof filename === "string" ? filename.replace(/\\/g, "") : "";
      if (cleanUrl && cleanFilename) {
        return `${cleanUrl}${cleanUrl.endsWith("/") ? "" : "/"}${cleanFilename}`;
      }
    }
    return typeof url === "string" ? url.replace(/\\/g, "") : "";
  }
  return "";
};

const pickImageUrl = (item: any) => {
  const logo =
    item?.logo ??
    item?.dyna?.logo ??
    item?.dyna?.cover ??
    item?.extra?.logo ??
    item?.extra?.extra?.logo;
  const image = item?.dyna?.image?.[0]?.url;
  const firstFile = item?.dyna?.files?.[0];

  return (
    normalizeMediaValue(logo) ||
    normalizeMediaValue(image) ||
    normalizeMediaValue(firstFile) ||
    normalizeMediaValue(item?.extra?.image_news)
  );
};

const defaultNormalizers = {
  categories: (data: any[]) =>
    data.map((item) => ({
      id: item.id,
      name: item.name ?? "",
      logo: pickImageUrl(item),
      meta_text: item.dyna?.meta_text ?? item.meta_text ?? "",
      description: item.dyna?.description ?? item.description ?? "",
    })),
  persons: (data: any[]) =>
    data.map((item) => ({
      id: item.id,
      name: item.name ?? "",
      nickname: item.dyna?.nickname ?? "",
      layout: item.dyna?.position ?? item.layout ?? "",
      introduction: item.dyna?.introduction ?? item.introduction ?? "",
      works: item.dyna?.work ?? "",
      logo: pickImageUrl(item),
      created_at: item.created_at,
    })),
  projects: (data: any[]) =>
    data.map((item) => ({
      id: item.id,
      name: item.name ?? "",
      meta_text: item.dyna?.meta_text ?? item.meta_text ?? "",
      logo: pickImageUrl(item),
      categories: item.dyna?.categories ?? item.categories ?? [],
      date_establishment: item.dyna?.date_establishment ?? null,
      created_at: item.created_at,
    })),
  items: (data: any[]) =>
    data.map((item) => {
      const fileImages = item?.dyna?.files ?? [];
      const images = fileImages.map((url: string, idx: number) => ({
        id: idx,
        url,
        caption: null,
        type: "image",
      }));

      let projects = item.dyna?.projects ?? [];

      if (projects && !Array.isArray(projects)) {
        projects = [projects];
      }

      projects = projects.map((project: any) => ({
        id: project.id,
        name: project.name ?? "",
        logo: pickImageUrl(project),

        categories: project.categories
          ? Array.isArray(project.categories)
            ? project.categories.map((cat: any) => ({
                id: cat.id,
                name: cat.name ?? "",
                logo: pickImageUrl(cat),
              }))
            : [
                {
                  id: project.categories.id,
                  name: project.categories.name ?? "",
                  logo: pickImageUrl(project.categories),
                },
              ]
          : [],
      }));

      return {
        id: item.id,
        name: item.name ?? "",
        content: item.dyna?.content ?? item.dyna?.meta_text ?? "",
        meta_text: item.dyna?.meta_text ?? "",
        subtitle: item.dyna?.under_title ?? "",
        under_title: item.dyna?.under_title ?? "",
        tags: item.dyna?.tags ?? [],
        projects,
        categories: item.dyna?.categories ?? [],
        images,
        cover: images?.[0]?.url ?? "",
        date_time: item.dyna?.date_time ?? "",
        release_date:
          item.dyna?.date_release ??
          item.extra?.date_release ??
          item.created_at ??
          "",
        created_at: item.created_at ?? "",
        updated_at: item.updated_at ?? "",
        is_featured: item.is_featured ?? false,
      };
    }),
};

const extractModuleData = (
  modules: LayoutModule[],
  selector: ModuleSelector | undefined,
) => {
  const target = modules.find((item) => {
    const moduleMatch = selector?.module
      ? item.module === selector.module
      : true;
    const aliasMatch = selector?.alias
      ? item.settings?.sources?.some(
          (source) => source.alias === selector.alias,
        )
      : true;
    return moduleMatch && aliasMatch;
  });

  return target?.data ?? [];
};

type UseLayoutContentParams<TContent> = {
  resource?: string;
  query?: LayoutQueryParams;
  extraQuery?: ExtraQueryParams;
  defaults?: Partial<Record<keyof TContent, any[]>>;
} & LayoutSelectors<TContent>;

export const useLayoutContent = <TContent extends Record<string, any[]>>(
  params: UseLayoutContentParams<TContent> = {},
) => {
  const {
    selectors = {},
    normalizers = {},
    defaults = {},
    resource = "content",
    query,
    extraQuery,
  } = params;

  const mergedQueryParams: LayoutQueryParams = {
    platform: query?.platform ?? 1,
    route: query?.route ?? "home",
    template: query?.template,
    item_id: query?.item_id,
    position: query?.position,
  };

  return useQuery<TContent & { rawModules: LayoutModule[] }, Error>({
    queryKey: [
      "layoutContent",
      resource,
      mergedQueryParams,
      selectors,
      extraQuery,
    ],
    queryFn: async () => {
      const response: LayoutResponse = await getLayoutResourceApi(
        resource,
        mergedQueryParams,
        extraQuery,
      );

      if (!response?.success) {
        throw new Error("خطا در دریافت داده صفحه");
      }

      const modules = response?.layout?.content ?? [];

      // Build result object by keys from normalizers/defaults/selectors
      const result: Record<string, any[]> = {};
      const selectorsRecord = selectors as Partial<
        Record<string, ModuleSelector>
      >;
      const normalizersRecord = normalizers as Partial<
        Record<string, NormalizeFn<any>>
      >;
      const defaultsRecord = defaults as Partial<Record<string, any[]>>;
      const keys = new Set([
        ...Object.keys(defaultNormalizers),
        ...Object.keys(defaults),
        ...Object.keys(normalizers),
        ...Object.keys(selectors),
      ]);

      keys.forEach((key) => {
        const selector = selectorsRecord[key] ?? { module: key };
        const data = extractModuleData(modules, selector as ModuleSelector);
        const normalizer =
          normalizersRecord[key] || (defaultNormalizers as any)[key];
        const normalized = normalizer ? normalizer(data) : data;
        result[key] =
          normalized?.length && Array.isArray(normalized)
            ? normalized
            : (defaultsRecord[key] ?? []);
      });

      return { ...(result as TContent), rawModules: modules };
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
